// The cart's product list: everything RareWorks sells at a fixed price.
// Mirrors alan-worksheets business/price-sheet.md (confirmed by Alan
// 2026-09-30); the display text for the service pages lives in prices.ts.
// Change a price on the sheet first, then in both files.
//
// taxable: Mississippi sales tax applies (alan-worksheets
// business/sales-tax-research.md, 2026-10-07): website builds and edits yes;
// hosting, social media, Google profile / SEO work no. Bundles that mix the
// two are marked taxable until the Department of Revenue answers how to split
// them. Checkout must not sell a taxable item until the sales tax permit is in
// hand and Stripe Tax is on.

// True since 2026-11-04: Alan said to sell these online and he handles the tax side (Stripe Tax stays off, no tax is added).
// The checkout function has its own copy of this switch (TAX_READY secret); both must agree.
export const TAX_READY = true;

import { HOSTING_SPECS } from "./prices";

export type Billing = "once" | "monthly" | "yearly";

export type CatalogItem = {
  id: string; // stable key: used in the cart, the order, and Stripe metadata
  category: "Websites" | "Care plans" | "Web address" | "Google and local SEO" | "Social media" | "Design" | "Other";
  name: string;
  cents: number;
  billing: Billing;
  desc: string;
  taxable: boolean;
  // more than one makes sense in a cart (extra pages, social graphics, hours)
  quantity?: boolean;
  // needs one of these in the cart or already on the account (a site needs a plan)
  needs?: string[];
  // the first 30 days of a monthly plan are free
  freeMonth?: boolean;
  // the fine print a buyer asks about, shown under the item in the cart
  specs?: string[];
  // questions asked in the cart before checkout
  ask?: string[];
  deliveredBy: "Alan" | "Kayla";
};

const SITE_QUESTIONS = [
  "Business name",
  "What does the business do?",
  "Do you already have a domain name? If so, which one?",
  "Anything the site must have (menu, booking, photos, a form)?",
];
const SOCIAL_QUESTIONS = [
  "Business name",
  "Links to your Facebook, Instagram, or TikTok pages",
  "What should we post about most?",
];
// Hosting only needs the customer's own domain (Alan, 2026-10-08): bring it, transfer it to us, or buy one.
const HOSTING_DOMAIN = "Your domain name. Tell us the one you have (we can connect it or transfer it to us), or the one you want us to register for you.";
const ADDRESS_QUESTIONS = ["The name you want in front (for example: mikesauto)", "Which of your sites is it for?"];
const DESIGN_QUESTIONS = ["Business name", "What should it say, and what is it for?", "Colors or styles you like"];

export const CATALOG: CatalogItem[] = [
  // Websites
  {
    id: "starter-site", category: "Websites", name: "Starter site (1 page)", cents: 40000, billing: "once",
    desc: "One page with your hours, phone, services or menu, photos, and a map. The site is yours. Needs a monthly plan.",
    taxable: true, needs: ["hosting-monthly", "hosting-yearly", "care-basic", "care-standard", "care-growth"],
    ask: SITE_QUESTIONS, deliveredBy: "Alan",
  },
  {
    id: "starter-managed", category: "Websites", name: "Starter site, managed", cents: 7500, billing: "monthly",
    desc: "No build fee. We build it, host it, and make your changes, up to 2 hours a month. Buy it from us any time for $400.",
    taxable: true, freeMonth: true, ask: SITE_QUESTIONS, deliveredBy: "Alan",
  },
  {
    id: "business-site", category: "Websites", name: "Business site (up to 10 pages)", cents: 180000, billing: "once",
    desc: "A page for each service and town, SEO setup, and forms that email you. The site is yours. Needs a care plan.",
    taxable: true, needs: ["care-basic", "care-standard", "care-growth"], ask: SITE_QUESTIONS, deliveredBy: "Alan",
  },
  {
    id: "business-managed", category: "Websites", name: "Business site, managed", cents: 15000, billing: "monthly",
    desc: "No build fee, 12-month minimum. Includes Standard care. Buy it from us any time for $1,800.",
    taxable: true, freeMonth: true, ask: SITE_QUESTIONS, deliveredBy: "Alan",
  },
  {
    id: "extra-page", category: "Websites", name: "Extra page", cents: 15000, billing: "once",
    desc: "One more page on a site we built. Sites over 10 pages are quoted.",
    taxable: true, quantity: true, ask: ["Which site, and what should the page cover?"], deliveredBy: "Alan",
  },

  // Care plans
  {
    id: "hosting-monthly", category: "Care plans", name: "Hosting only, monthly", cents: 500, billing: "monthly",
    desc: "Hosting and security for a site you own, on your own domain name. You make your own changes.",
    taxable: false, freeMonth: true, specs: HOSTING_SPECS, ask: [HOSTING_DOMAIN], deliveredBy: "Alan",
  },
  {
    id: "hosting-yearly", category: "Care plans", name: "Hosting only, yearly", cents: 5000, billing: "yearly",
    desc: "A year of hosting on your own domain name, paid up front. Two months free compared with monthly.",
    taxable: false, specs: HOSTING_SPECS, ask: [HOSTING_DOMAIN], deliveredBy: "Alan",
  },
  {
    id: "care-basic", category: "Care plans", name: "Basic care", cents: 5000, billing: "monthly",
    desc: "Hosting (2 GB, unmetered bandwidth, SSL), security, backups, and up to 1 hour of edits a month.",
    taxable: true, freeMonth: true, deliveredBy: "Alan",
  },
  {
    id: "care-standard", category: "Care plans", name: "Standard care", cents: 10000, billing: "monthly",
    desc: "Basic, plus up to 2 hours of edits and 2 Google Business Profile posts a month.",
    taxable: true, freeMonth: true, deliveredBy: "Alan",
  },
  {
    id: "care-growth", category: "Care plans", name: "Growth care", cents: 20000, billing: "monthly",
    desc: "Standard, plus up to 4 hours of edits, Google Business Profile management, local SEO, keyword tracking, and a monthly report.",
    taxable: true, freeMonth: true, deliveredBy: "Alan",
  },

  // Web address: a short address on one of our domains for a site we host. The free address is
  // leads.rare-works.com/<name>/; a customer's own domain is registered at cost (not a cart item).
  // Yearly and non-refundable (Alan, 2026-10-08): he pays for the domains by the year, and the price ends in .99 to cover Stripe's cut.
  {
    id: "address-com", category: "Web address", name: "RareWorks address: yourname.rare-works.com", cents: 399, billing: "yearly",
    desc: "A short address for a site we host, paid by the year. Not refundable. Your site's free address is leads.rare-works.com/yourname.",
    taxable: false, ask: ADDRESS_QUESTIONS, deliveredBy: "Alan",
  },
  {
    id: "address-net", category: "Web address", name: "RareWorks address: yourname.rare-works.net", cents: 299, billing: "yearly",
    desc: "The same short address on our .net, paid by the year. Not refundable.",
    taxable: false, ask: ADDRESS_QUESTIONS, deliveredBy: "Alan",
  },
  {
    id: "address-xyz", category: "Web address", name: "RareWorks address: yourname.rare-works.xyz", cents: 199, billing: "yearly",
    desc: "The same short address on our .xyz, paid by the year. Not refundable.",
    taxable: false, ask: ADDRESS_QUESTIONS, deliveredBy: "Alan",
  },

  // Google and local SEO
  {
    id: "gbp-setup", category: "Google and local SEO", name: "Google Business Profile + local SEO setup", cents: 30000, billing: "once",
    desc: "Category, services, products, photos, description, and fixes to what's already there.",
    taxable: false, ask: ["Business name", "Link to your Google Business Profile, if you have one"], deliveredBy: "Alan",
  },
  {
    id: "gbp-manage", category: "Google and local SEO", name: "Google Business Profile + local SEO management", cents: 15000, billing: "monthly",
    desc: "Posts, updates, keyword tracking, and a monthly report. Already included in Growth care.",
    taxable: false, freeMonth: true, ask: ["Business name", "Link to your Google Business Profile"], deliveredBy: "Alan",
  },

  // Social media
  {
    id: "social-starter", category: "Social media", name: "Social media: Starter", cents: 40000, billing: "monthly",
    desc: "8 to 12 posts a month on 2 platforms, with graphics and captions, scheduled and posted for you.",
    taxable: false, freeMonth: true, ask: SOCIAL_QUESTIONS, deliveredBy: "Kayla",
  },
  {
    id: "social-standard", category: "Social media", name: "Social media: Standard", cents: 70000, billing: "monthly",
    desc: "16 to 20 posts a month on 3 platforms, stories, and replies to comments.",
    taxable: false, freeMonth: true, ask: SOCIAL_QUESTIONS, deliveredBy: "Kayla",
  },
  {
    id: "social-full", category: "Social media", name: "Social media: Full", cents: 100000, billing: "monthly",
    desc: "Standard, plus ad campaign management. Ad spend is paid by you, straight to the platform.",
    taxable: false, freeMonth: true, ask: SOCIAL_QUESTIONS, deliveredBy: "Kayla",
  },

  // Design
  {
    id: "logo", category: "Design", name: "Logo", cents: 30000, billing: "once",
    desc: "A logo for your business, with 2 rounds of changes.", taxable: false, ask: DESIGN_QUESTIONS, deliveredBy: "Kayla",
  },
  {
    id: "brand-package", category: "Design", name: "Brand package", cents: 80000, billing: "once",
    desc: "Logo and its versions, colors, fonts, a business card, and a social media kit.",
    taxable: false, ask: DESIGN_QUESTIONS, deliveredBy: "Kayla",
  },
  {
    id: "business-cards", category: "Design", name: "Business card design", cents: 7500, billing: "once",
    desc: "The design file for your cards. Printing is not included.", taxable: false, ask: DESIGN_QUESTIONS, deliveredBy: "Kayla",
  },
  {
    id: "flyer", category: "Design", name: "Flyer", cents: 7500, billing: "once",
    desc: "One flyer design, ready to print or post.", taxable: false, quantity: true, ask: DESIGN_QUESTIONS, deliveredBy: "Kayla",
  },
  {
    id: "menu-sign", category: "Design", name: "Menu or sign", cents: 15000, billing: "once",
    desc: "One menu or sign design, ready to print.", taxable: false, quantity: true, ask: DESIGN_QUESTIONS, deliveredBy: "Kayla",
  },
  {
    id: "social-graphic", category: "Design", name: "Social media graphic", cents: 3000, billing: "once",
    desc: "One graphic sized for your social pages.", taxable: false, quantity: true, ask: DESIGN_QUESTIONS, deliveredBy: "Kayla",
  },
  {
    id: "ad-card", category: "Design", name: "Ad card", cents: 3500, billing: "once",
    desc: "One ad card design.", taxable: false, quantity: true, ask: DESIGN_QUESTIONS, deliveredBy: "Kayla",
  },

  // Other
  {
    id: "hourly", category: "Other", name: "One-off work, 1 hour", cents: 7500, billing: "once",
    desc: "Edits, fixes, and anything not listed. Buy the hours you need.",
    taxable: true, quantity: true, ask: ["Which site, and what do you need done?"], deliveredBy: "Alan",
  },
];

// Sold by quote, never through the cart.
export const QUOTE_ONLY = [
  { name: "Custom builds", desc: "Booking, customer portals, and apps. Estimated at $85 an hour, 50% deposit.", href: "/custom-builds.html" },
  { name: "Sites over 10 pages", desc: "Quoted on scope, at about $150 a page.", href: "/contact.html" },
  { name: "Your own domain name", desc: "Like yourbusiness.com. At cost, about $11 a year for a .com, added to your bill once you pick one. You own it.", href: "/contact.html" },
];

export const byId = Object.fromEntries(CATALOG.map((i) => [i.id, i])) as Record<string, CatalogItem>;
